import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import {
    FiChecklistHelperService,
    FiChecklistModuleDiscardChangesDialogData,
    IChecklistCheckpointPlaceholderLabelData,
} from '@fi-shared/checklist/fi-checklist-helper.service';
import { IChecklistCheckpointCommonData } from '@fi-shared/checklist/checklist';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { ObjectTypes } from '@fi-shared/types';
import { IChecklistV2, ICheckpointV2 } from '@fi-shared/checklist/checklist-v2';
import { IFiDialogOptions } from '@fi-shared/fi-dialogs/fi-dialogs';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { Subject } from 'rxjs';

interface IChecklistCheckpointDialogData extends IChecklistCheckpointPlaceholderLabelData {
    item: ICheckpointV2 | IChecklistV2;
    headingLabel: string;
    descriptionLabel: string;
    showAttachmentsArea: boolean;
    templateMode?: boolean;
}

@Component({
    selector: 'checklist-common-data-dialog',
    template: `
        <p class="dialog-heading-label bold">{{ data.headingLabel }}</p>
        <p class="dialog-description-label">{{ data.descriptionLabel }}</p>

        <fi-name-description-input
            [(ngModel)]="item.Names"
            [(mediaBankFileReferences)]="item.MediaBankFileReferences"
            [(descriptions)]="item.Descriptions"
            [selectedLanguageId]="getUserPreferredContentLanguage()"
            [namePlaceholderTextId]="data.namePlaceholderTextId"
            [namePlaceholderHintTextId]="data.namePlaceholderHintTextId"
            [descriptionPlaceholderHintTextId]="data.descriptionPlaceholderHintTextId"
            [showAttachmentsArea]="data.showAttachmentsArea"
            [objectType]="currentObjectType"
        />
    `,
    styleUrls: ['./checklist-common-data-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist-checklist-common-data-dialog' },
    standalone: false,
})
export class FiChecklistCommonDataDialogComponent
    extends FiDialogsContentBase<IChecklistCheckpointDialogData, ICheckpointV2 | IChecklistV2>
    implements OnInit, OnDestroy
{
    get item(): ICheckpointV2 | IChecklistV2 {
        return this.data.item;
    }

    get templateMode(): boolean {
        return this.data.templateMode;
    }

    protected readonly currentObjectType = this.data.templateMode
        ? ObjectTypes.Checklist2Template
        : ObjectTypes.Checklist2;

    private fiValuesComparison: FiValuesComparison<ICheckpointV2 | IChecklistV2>;

    ngOnInit() {
        this.setServices();
        this.fiValuesComparison = new FiValuesComparison(() => this.item);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    getUserPreferredContentLanguage(): number {
        return this.fiUserDataService.getContentLanguage();
    }

    getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        const dialogData: IFiDialogOptions<null, null> = this.templateMode
            ? this.fiChecklistHelperService.getChecklistDiscardChangesDialogData(
                  this.item.Id,
                  ObjectTypes.Checklist2Template,
              )
            : {};
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged(), dialogData);
    }

    isYesButtonDisabled(): boolean {
        return !this.item.Names.find((name: IContentLanguageLabelModel) => {
            return name.ContentLanguageId === this.getUserPreferredContentLanguage();
        })?.Value;
    }

    getResult(): ICheckpointV2 | IChecklistV2 {
        return this.item;
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    private fiUserDataService: FiUserDataService;
    private fiDialogsService: FiDialogsService;
    private fiChecklistHelperService: FiChecklistHelperService;

    private setServices() {
        this.fiUserDataService = this.injector.get(FiUserDataService);
        this.fiDialogsService = this.injector.get(FiDialogsService);
        this.fiChecklistHelperService = this.injector.get(FiChecklistHelperService);
    }
}
